/**
 * LE COMBAT — un récit qui s'écrit, tour par tour, dans la même partition que le nœud.
 *
 * Il se lit comme le reste du jeu : pas de scène séparée, pas de barres qui clignotent. Ce qui le
 * distingue d'un nœud, c'est que CHAQUE JET EST MONTRÉ — le d20, le total, le seuil. Un permadeath
 * sur un jet caché est insupportable ; le moteur est déterministe, il peut tout justifier, alors il
 * justifie.
 *
 * TROIS ZONES, ET LES ACTIONS NE BOUGENT PAS. Les adversaires en haut, le récit au milieu — qui
 * défile, et se cale toujours sur le dernier coup —, les actions en bas, à leur place. Un bouton
 * d'attaque qui se déplace parce qu'une ligne de récit s'est ajoutée est un bouton qui fait cliquer
 * sur « fuir », et dans ce jeu-là on ne recommence pas.
 *
 * LA RÉACTION SE DÉCLARE AVEC L'ACTION (brief, « Décisions prises ») : « je frappe, et je garde de
 * quoi esquiver ». C'est pour ça que la case à cocher est ici, à côté des actions, et non dans une
 * fenêtre qui surgirait au coup adverse — ce qui casserait la lecture, et serait injouable en mains
 * libres.
 *
 * ON CLIQUE UN ADVERSAIRE POUR LE VISER ; on ouvre sa fiche pour SAVOIR CE QU'ON AFFRONTE — son
 * portrait, son arme, ce qu'il inflige en touchant. Jamais ses caractéristiques : celles-là
 * s'apprennent en le frappant, et c'est le jeu.
 */
import { useEffect, useRef, useState } from "react";

import type { ActionCombat, CombatEnVue, CombattantEnVue, Etat, HerosEnVue, ObjetConnu, Regles } from "../../api/types.js";
import { De } from "../../composants/De.js";
import { Effigie } from "../../composants/Effigie.js";
import { Glyphe } from "../../composants/Glyphe.js";
import { Illustration } from "../../composants/Illustration.js";
import { Jauge } from "../../composants/Jauge.js";
import { Popin } from "../../composants/Popin.js";
import { Voix } from "../../composants/Voix.js";
import { composeLeRecit, ID_DU_HEROS, type LigneDeRecit } from "../../recit/combat.js";
import type { EvenementCombat } from "../../api/types.js";
import { ETATS, ditLeConsommable, nomDObjet } from "../../lexique/mots.js";
import { t } from "../../lexique/langue.js";

function Ligne({ ligne }: { ligne: LigneDeRecit }) {
  if (ligne.ouvreLeTour !== undefined) {
    return (
      <li className="recit__tour">
        <span className="capitales">{ligne.texte}</span>
      </li>
    );
  }
  return (
    <li className={`recit__ligne recit__ligne--${ligne.ton}`}>
      <span>{ligne.texte}</span>
      {ligne.jet ? (
        <span className="recit__jet">
          <De
            jet={ligne.jet.d20}
            total={ligne.jet.total}
            dd={ligne.jet.dd}
            reussi={ligne.jet.reussi}
            critique={ligne.jet.critique}
            echecCritique={ligne.jet.echecCritique}
          />
        </span>
      ) : null}
    </li>
  );
}

/**
 * LE PORTRAIT D'UN ADVERSAIRE — l'image posée si elle existe, l'EFFIGIE sinon (design §3).
 *
 * L'ordre n'est pas négociable : une illustration du §6 passe toujours devant. Mais son absence ne
 * laisse plus un vide ici — on ne frappe pas un nom pendant six tours.
 */
function PortraitDAdversaire({
  combattant,
  taille,
  className
}: {
  combattant: CombattantEnVue;
  taille: number;
  className?: string;
}) {
  if (combattant.illustration) {
    return <Illustration url={combattant.illustration} legende={combattant.nom} forme="portrait" className={className} />;
  }
  return (
    <Effigie
      signalement={{
        espece: combattant.espece,
        nom: combattant.nom,
        arme: combattant.arme,
        inflige: combattant.inflige,
        pvMax: combattant.pvMax
      }}
      tombe={combattant.pv <= 0}
      taille={taille}
      className={className}
    />
  );
}

/** LA FICHE D'UN ADVERSAIRE. Ce qu'on voit de lui, et rien de plus : le serveur ne renvoie pas ses
 * caractéristiques, et l'interface n'en invente pas. */
function FicheDAdversaire({ combattant, surFermeture }: { combattant: CombattantEnVue | null; surFermeture: () => void }) {
  return (
    <Popin
      ouvert={combattant !== null}
      titre={combattant?.nom ?? ""}
      glyphe={<Glyphe nom="crane" taille={16} />}
      surFermeture={surFermeture}
    >
      {combattant ? (
        <>
          <PortraitDAdversaire combattant={combattant} taille={168} />
          <div className="rang rang--espace">
            <span className="capitales">{t("Points de vie")}</span>
            <span className="nombre">
              {Math.max(0, combattant.pv)} / {combattant.pvMax}
            </span>
          </div>
          <Jauge valeur={Math.max(0, combattant.pv)} maximum={combattant.pvMax} espece="pv" largeur={280} />

          {combattant.arme ? (
            <div className="rang rang--espace" style={{ marginTop: "var(--e4)" }}>
              <span className="capitales">{t("Arme")}</span>
              <span>{combattant.arme}</span>
            </div>
          ) : null}

          {combattant.inflige ? (
            <p className="mention mention--verrou" style={{ marginTop: "var(--e3)" }}>
              <Glyphe nom={combattant.inflige} taille={14} />{" "}
              {t("Un coup qui porte peut infliger {etat} — {effet}", {
                etat: ETATS[combattant.inflige].nom.toLowerCase(),
                effet: ETATS[combattant.inflige].effet
              })}
            </p>
          ) : null}

          {combattant.etats.length ? (
            <div className="rang rang--serre" style={{ marginTop: "var(--e3)", flexWrap: "wrap" }}>
              {combattant.etats.map((e) => (
                <span key={e.etat} className="etat" title={ETATS[e.etat].effet}>
                  <Glyphe nom={e.etat} taille={14} />
                  {ETATS[e.etat].nom}
                </span>
              ))}
            </div>
          ) : null}

          <p className="faible" style={{ marginTop: "var(--e4)", marginBottom: 0 }}>
            {t("Ce qu'il vaut au combat s'apprend en le frappant.")}
          </p>
        </>
      ) : null}
    </Popin>
  );
}

export function Combat({
  combat,
  heros,
  catalogue,
  evenements,
  capacite,
  chiffres,
  bloque,
  surAction
}: {
  combat: CombatEnVue;
  heros: HerosEnVue;
  catalogue: Map<string, ObjetConnu>;
  evenements: EvenementCombat[];
  capacite: { nom: string; cout: number } | null;
  /** Ce que chaque action fait, en chiffres — servi par `/public/regles`, jamais recopié ici : une
   * constante d'équilibrage vit dans `regles.ts` et nulle part ailleurs (CLAUDE.md). Nul tant que
   * la table n'est pas arrivée : le bouton dit alors son effet sans le chiffrer, ce qui reste vrai. */
  chiffres: Regles["combat"] | null;
  bloque: boolean;
  surAction: (action: ActionCombat) => void;
}) {
  const adverses = combat.combattants.filter((c) => c.camp === "adverse");
  const vivants = adverses.filter((c) => c.pv > 0);
  const [cible, poseLaCible] = useState<string | null>(null);
  const [garde, poseLaGarde] = useState(true);
  const [detaille, poseLeDetail] = useState<string | null>(null);
  const parchemin = useRef<HTMLOListElement>(null);

  const cibleChoisie = vivants.find((c) => c.id === cible)?.id ?? vivants[0]?.id ?? null;
  const noms = new Map(combat.combattants.map((c) => [c.id, c.nom]));
  const lignes = composeLeRecit(evenements, noms);
  const reaction = garde ? ("esquive" as const) : ("aucune" as const);

  const consommables = heros.sac.filter((pile) => catalogue.get(pile.objetId)?.consommable);
  /** LES GRIMOIRES QU'ON PORTE (§3.2). Ils ne se consomment pas : un seul suffit à changer tous les
   * combats d'une aventure, et c'est pourquoi ils sont rares. */
  /** L'ESSENCE QU'ON A VRAIMENT, ICI ET MAINTENANT. Le combat travaille sur une copie et ne la
   * reporte au héros qu'au dénouement (§3.2) : lire la fiche pendant l'échange, c'est lire l'état
   * d'avant — et proposer un sort qu'on n'a plus les moyens de lancer. */
  const moi = combat.combattants.find((c) => c.camp === "heros");
  const essenceDisponible = moi?.essence ?? heros.essence;
  const grimoires = heros.sac
    .map((pile) => ({ objetId: pile.objetId, sort: catalogue.get(pile.objetId)?.sort }))
    .filter((g): g is { objetId: string; sort: NonNullable<NonNullable<ObjetConnu["sort"]>> } => Boolean(g.sort));

  // LE RÉCIT SE CALE SUR LE DERNIER COUP. Un combat de douze tours dont on ne voit que le premier
  // est un combat qu'on joue à l'aveugle — et l'on ne fait défiler QUE ce cadre-là, jamais la page.
  useEffect(() => {
    const cadre = parchemin.current;
    if (cadre) cadre.scrollTop = cadre.scrollHeight;
  }, [lignes.length]);

  return (
    <section className="combat">
      <header className="combat__entete">
        <div className="combat__titre">
          <h2>
            {t("Combat")} <span className="faible nombre">{t("— tour {tour}", { tour: combat.tour })}</span>
          </h2>
          <Voix texte={lignes.map((l) => l.texte).join(". ")} titre={t("Relire le combat à voix haute")} />
        </div>
        <div className="combat__adversaires">
          {adverses.map((c) => {
            const mort = c.pv <= 0;
            const vise = c.id === cibleChoisie && !mort;
            return (
              <div key={c.id} className={`adversaire${mort ? " adversaire--tombe" : ""}${vise ? " adversaire--vise" : ""}`}>
                <button
                  type="button"
                  className="adversaire__viser"
                  onClick={() => poseLaCible(c.id)}
                  disabled={mort || bloque}
                  aria-pressed={vise}
                  title={mort ? t("À terre") : t("Viser {qui}", { qui: c.nom })}
                >
                  <PortraitDAdversaire combattant={c} taille={88} className="adversaire__portrait" />
                  <span className="adversaire__nom">{c.nom}</span>
                  <span className="nombre faible">
                    {Math.max(0, c.pv)} / {c.pvMax}
                  </span>
                  <Jauge valeur={Math.max(0, c.pv)} maximum={c.pvMax} espece="pv" largeur={128} />
                  {c.etats.length ? (
                    <span className="rang rang--serre">
                      {c.etats.map((e) => (
                        <span key={e.etat} className="etat etat--petit" title={ETATS[e.etat].effet}>
                          <Glyphe nom={e.etat} taille={13} />
                          {ETATS[e.etat].nom}
                        </span>
                      ))}
                    </span>
                  ) : null}
                  {mort ? <span className="capitales">{t("À terre")}</span> : null}
                </button>
                <button
                  type="button"
                  className="puce puce--sourde adversaire__fiche"
                  onClick={() => poseLeDetail(c.id)}
                  aria-haspopup="dialog"
                >
                  {t("Ce qu'on en sait")}
                </button>
              </div>
            );
          })}
        </div>
      </header>

      <ol className="recit" ref={parchemin}>
        {lignes.map((ligne, index) => (
          <Ligne key={index} ligne={ligne} />
        ))}
        {/* LE COMBAT REPRIS. Le récit se compose des événements de CETTE session : un joueur qui
            revient sur un combat commencé hier n'en a aucun. Plutôt qu'un cadre vide, on lui dit où
            il en est — le journal de bord, lui, garde la trace longue (§12). */}
        {!lignes.length ? (
          <li className="recit__ligne recit__ligne--souffle">
            <span>
              {t(
                "L'échange est engagé — tour {tour}. Les coups déjà portés sont au journal ; ceux qui viennent s'écriront ici.",
                { tour: combat.tour }
              )}
            </span>
          </li>
        ) : null}
      </ol>

      {combat.fin ? null : (
        <div className="actions">
          {/* LA RÉACTION SE DÉCLARE AVEC L'ACTION (brief, « Décisions prises »). La case était là
              depuis le lot 3, sans une phrase pour dire ce qu'elle fait : on la laissait cochée sans
              savoir. C'est le cœur du système de combat, il est donc expliqué à sa place. */}
          <label className="rang rang--serre actions__reaction">
            <input type="checkbox" checked={garde} onChange={(e) => poseLaGarde(e.target.checked)} />
            <span>
              {t("Garder de quoi esquiver")}{" "}
              <span className="faible">
                {t("— vous frappez ET vous gardez une esquive, une seule, jouée après le coup adverse")}
              </span>
            </span>
          </label>

          {/* DEUX COLONNES QUI S'OPPOSENT (design §3). Frapper, ou se garder : c'est l'arbitrage du
              tour, et sept boutons en grille ne le montraient pas. Chaque bouton porte SON effet,
              avec le chiffre servi par `/public/regles` — jamais recopié ici. */}
          <div className="actions__partition">
            <div className="actions__colonne">
              <span className="actions__enseigne capitales">
                <Glyphe nom="epee" taille={14} /> {t("Frapper")}
              </span>

              <button
                type="button"
                className="action action--maitresse"
                disabled={bloque || !cibleChoisie}
                onClick={() => cibleChoisie && surAction({ type: "attaquer", cible: cibleChoisie, reaction })}
              >
                <span className="action__nom">{t("Attaquer")}</span>
                <span className="action__effet">{t("d20 + Attaque contre sa défense")}</span>
              </button>

              <button
                type="button"
                className="action"
                disabled={bloque || !cibleChoisie}
                onClick={() => cibleChoisie && surAction({ type: "attaque_puissante", cible: cibleChoisie, reaction })}
              >
                <span className="action__nom">{t("Attaque puissante")}</span>
                <span className="action__effet">
                  {chiffres
                    ? t("−{malus} au toucher, dégâts doublés si le coup porte", {
                        malus: chiffres.malusAttaquePuissante
                      })
                    : t("malus au toucher, dégâts doublés si le coup porte")}
                </span>
              </button>

              {capacite ? (
                <button
                  type="button"
                  className="action"
                  disabled={bloque}
                  onClick={() => surAction({ type: "capacite", cible: cibleChoisie ?? undefined, reaction })}
                >
                  <span className="action__nom">{capacite.nom}</span>
                  <span className="action__effet">
                    {capacite.cout
                      ? t("{cout} d'Essence — la capacité de votre archétype", { cout: capacite.cout })
                      : t("sans coût d'Essence — la capacité de votre archétype")}
                  </span>
                </button>
              ) : null}

              {/* LES SORTS QU'ON PORTE (§3.2). Un bouton par grimoire dans le sac : chacun porte son
                  coût et ce qu'il fait, comme toutes les actions de combat. Ils sont dans la colonne
                  « frapper » parce que c'est ce qu'ils font — ce ne sont ni des gardes, ni des
                  objets qu'on avale. Sans Essence, le bouton reste LISIBLE et désaturé (design §3) :
                  savoir qu'on aurait pu est une information. */}
              {grimoires.map((g) => (
                <button
                  key={g.objetId}
                  type="button"
                  className="action"
                  disabled={bloque || !cibleChoisie || essenceDisponible < g.sort.cout}
                  title={essenceDisponible < g.sort.cout ? t("Pas assez d'Essence") : undefined}
                  onClick={() =>
                    cibleChoisie && surAction({ type: "lancer_sort", objetId: g.objetId, cible: cibleChoisie, reaction })
                  }
                >
                  <span className="action__nom">{g.sort.nom}</span>
                  <span className="action__effet">
                    {g.sort.degats
                      ? t("{cout} d'Essence — {degats} + Essence, ignore l'armure", {
                          cout: g.sort.cout,
                          degats: g.sort.degats
                        })
                      : t("{cout} d'Essence — sans dégât", { cout: g.sort.cout })}
                    {g.sort.etat ? `, ${ETATS[g.sort.etat as Etat]?.nom.toLowerCase() ?? g.sort.etat}` : ""}
                  </span>
                </button>
              ))}
            </div>

            <div className="actions__colonne">
              <span className="actions__enseigne capitales">
                <Glyphe nom="bouclier" taille={14} /> {t("Se garder")}
              </span>

              <button type="button" className="action" disabled={bloque} onClick={() => surAction({ type: "parer", reaction })}>
                <span className="action__nom">{t("Parer")}</span>
                <span className="action__effet">
                  {chiffres
                    ? t("+{bonus} en défense jusqu'à votre prochain tour — vous ne frappez pas", {
                        bonus: chiffres.bonusParade
                      })
                    : t("défense renforcée jusqu'à votre prochain tour — vous ne frappez pas")}
                </span>
              </button>

              <button type="button" className="action" disabled={bloque} onClick={() => surAction({ type: "esquiver" })}>
                <span className="action__nom">{t("Esquiver")}</span>
                <span className="action__effet">
                  {chiffres
                    ? t("+{bonus} à l'esquive, et l'esquive reste gardée — vous ne frappez pas", {
                        bonus: chiffres.bonusPostureEsquive
                      })
                    : t("esquive renforcée, et l'esquive reste gardée — vous ne frappez pas")}
                </span>
              </button>

              <button type="button" className="action" disabled={bloque} onClick={() => surAction({ type: "se_cacher", reaction })}>
                <span className="action__nom">{t("Se cacher")}</span>
                <span className="action__effet">{t("jet d'Agilité contre leur vigilance — caché, on frappe mieux")}</span>
              </button>

              <button type="button" className="action action--fuite" disabled={bloque} onClick={() => surAction({ type: "fuir" })}>
                <span className="action__nom">{t("Fuir")}</span>
                <span className="action__effet">{t("on ne revient pas dans ce combat, et le butin reste ici")}</span>
              </button>
            </div>
          </div>

          {consommables.length ? (
            <div className="actions__objets">
              <span className="capitales">{t("Boire, panser")}</span>
              <div className="rang rang--serre">
                {consommables.map((pile) => {
                  const quoi = catalogue.get(pile.objetId)?.consommable ?? null;
                  return (
                    <button
                      key={pile.objetId}
                      type="button"
                      className="puce"
                      disabled={bloque}
                      // CE QUE ÇA FAIT, ICI AUSSI. Le nom seul obligeait à se souvenir de ce que
                      // rend une grande potion — au tour où l'on décide de la boire ou de frapper.
                      title={quoi ? t("{effet} — et cela coûte le tour", { effet: ditLeConsommable(quoi) }) : undefined}
                      onClick={() => surAction({ type: "utiliser_objet", objetId: pile.objetId, reaction })}
                    >
                      {nomDObjet(pile.objetId, catalogue)} <span className="nombre faible">{t("×")}{pile.quantite}</span>
                      {quoi ? <span className="action__effet">{ditLeConsommable(quoi)}</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {!combat.aVous ? (
            <p className="faible" role="status">
              {t("Ce n'est pas votre tour — l'adversaire joue.")}
            </p>
          ) : null}
        </div>
      )}

      <FicheDAdversaire
        combattant={adverses.find((c) => c.id === detaille) ?? null}
        surFermeture={() => poseLeDetail(null)}
      />
    </section>
  );
}

export { ID_DU_HEROS };
